import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';

import { actuators as actuatorsList } from "../../actuators";
import { controllers as controllersList } from "../../controllers";
import { addPublicURL } from "../../Functions/AddPublicURL";
import { formatEntityId, formatOptionalEntityId } from "../../Functions/FormatEntityId";
import { getIconPath } from '../../Functions/GetIconPath';
import { setSelectedActuator } from "../../redux/reducers/actuatorsRedurer";
import { setSelectedAppliance } from "../../redux/reducers/applianceReducer";
import { setApplianceLabel, setShowActuatorDetail, setShowApplianceDetail, setShowDeviceDetail, setShowThermostatDetail } from "../../redux/reducers/appReducer";
import { setSelectedDeviceId } from "../../redux/reducers/controllersReducer";
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ActuatorStateI } from "../../redux/types/app";
import { DeviceI } from "../../redux/types/controllers";
import Button from "../Button/Button";

import styles from "./ApplianceDetail.module.scss";

const ApplianceDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const actuators = useAppSelector((state) => state.app.actuators);
    const controllers = useAppSelector((state) => state.controllers.devices);
    const selectedAppliance = useAppSelector((state) => state.appliances.appliance);
    const allAppliances = useAppSelector((state) => state.app.appliances);
    const applianceActuator = actuators.find((actuator) => actuator.id === selectedAppliance.actuator);
    const connectedControllers = controllers.filter((controller) => selectedAppliance.connectedTo.includes(controller.id));
    const hasConnectedActuator = Boolean(applianceActuator?.connectedAppliancesByChannel?.some(
        (channel) => channel.applianceIds.includes(selectedAppliance.id)
    ));
    const hasConnectedController = connectedControllers.some((controller) =>
        controller.iconsData.some((icon) => icon.applianceId?.includes(selectedAppliance.id))
    );
    const usage = actuatorsList.find((actuator) => actuator.type === applianceActuator?.type)?.usage || 'N/A';
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const [labelValue, setLabelValue] = useState(selectedAppliance.label);

    useEffect(() => {
        if (!isEditingLabel) {
            setLabelValue(selectedAppliance.label);
        }
    }, [isEditingLabel, selectedAppliance.label]);

    const saveLabel = () => {
        const trimmedLabel = labelValue.trim();
        if (!trimmedLabel) {
            setLabelValue(selectedAppliance.label);
            setIsEditingLabel(false);
            return;
        }

        dispatch(setApplianceLabel({ id: selectedAppliance.id, label: trimmedLabel }));
        setLabelValue(trimmedLabel);
        setIsEditingLabel(false);
    };

    const handleLabelKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            saveLabel();
        }
        if (event.key === 'Escape') {
            setLabelValue(selectedAppliance.label);
            setIsEditingLabel(false);
        }
    };

    const handleControllerClick = (controllerId: number, deviceType: string) =>{
        switch(deviceType){
            case 'controller':
                dispatch(setSelectedDeviceId(controllerId));
                dispatch(setShowDeviceDetail(true));
            break;
            case 'thermostat':
                dispatch(setShowThermostatDetail(true));
            break;
        }
        dispatch(setSelectedDeviceId(controllerId));
        dispatch(setShowDeviceDetail(true));
    }
    const handleActuatorClick = (actuator: ActuatorStateI) =>{
        dispatch(setSelectedActuator(actuator));
        dispatch(setShowActuatorDetail(true));
    }

    useEffect(() => {
        if (!selectedAppliance) {
            toast.error(t('applianceDetail.errorNoApplianceSelected'));
            dispatch(setShowApplianceDetail(false));
        }
    }, [selectedAppliance, dispatch, t]);

    useEffect(() => {
        const applianceRef = allAppliances.find((appliance) => appliance.id === selectedAppliance.id);
        if(applianceRef){
            const nextSelectedAppliance = {
                id: applianceRef.id,
                label: applianceRef.label,
                icon: applianceRef.icon,
                type: applianceRef.type,
                applianceType: applianceRef.applianceType,
                state: applianceRef.state || '',
                actuator: applianceRef.actuator || 0,
                connectedTo: applianceRef.connectedTo || []
            };

            const isSameAppliance =
                selectedAppliance.id === nextSelectedAppliance.id &&
                selectedAppliance.label === nextSelectedAppliance.label &&
                selectedAppliance.icon === nextSelectedAppliance.icon &&
                selectedAppliance.type === nextSelectedAppliance.type &&
                selectedAppliance.applianceType === nextSelectedAppliance.applianceType &&
                (selectedAppliance.state || '') === nextSelectedAppliance.state &&
                (selectedAppliance.actuator || 0) === nextSelectedAppliance.actuator &&
                JSON.stringify(selectedAppliance.connectedTo || []) === JSON.stringify(nextSelectedAppliance.connectedTo);

            if (!isSameAppliance) {
                dispatch(setSelectedAppliance(nextSelectedAppliance));
            }
        }
    }, [allAppliances, selectedAppliance, dispatch]);

    const getConnectedActuatorInformation = (actuator: ActuatorStateI | undefined) => {
        if (!actuator || !actuator.connectedAppliancesByChannel) {
            return "-";
        }
        const channel = actuator.connectedAppliancesByChannel.find((ch) => ch.applianceIds.includes(selectedAppliance.id));
        if (!channel) {
            return "-";
        }
        return `${actuator.id ? formatEntityId('actuator', actuator.id) : '-'} / ch.${channel.channel}`;
    }
    const getConnectedControllerInformation = (controllers: DeviceI[] | undefined) => {
        if(!controllers || controllers.length === 0){
            return "-";
        }
        const information: string[] = [];
        controllers.forEach((controller) => {
            if(controller.iconsData.map(icon => icon.applianceId?.includes(selectedAppliance.id)).includes(true)){
                let controllerInformation = `${controller.id ? formatEntityId('controller', controller.id) : '-'} / `;
                const position = controller.iconsData.map(icon => icon.applianceId?.includes(selectedAppliance.id) ? icon.position : null);
                const buttons = controllersList.filter(cRef => cRef.name === controller.name)[0].buttonCodes ?? null;
                if(buttons){
                    const buttonPositions = position.map((pos) => {
                        if(pos !== null){
                            return (buttons as Record<number, string>)[pos] ?? '';
                        }
                        return '';
                    }).filter(pos => pos !== '').join(', ');
                    controllerInformation += buttonPositions !== '' ? buttonPositions : '';
                }
                information.push(controllerInformation);
            }
        });
        return information.length > 0 ? information.join("\n") : "-";
    }

    return (
        <>
            <div className={styles.ApplianceDetail}>
                <div className={styles.ApplianceDetail__wrapper}>
                    <h2>{t('applianceDetail.heading')}</h2>
                    <p>{t('applianceDetail.description')}</p>
                    <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setShowApplianceDetail(false))} className={styles.ApplianceDetail__back} />

                    <div className={styles.ApplianceDetail__content}>
                        <div className={styles.ApplianceDetail__content__appliance}>
                            <div className={styles.ApplianceDetail__content__appliance__heading}>
                                <span></span>
                                <span>{t('applianceDetail.table.id')}</span>
                                <span>{t('applianceDetail.table.label')}</span>
                                <span>{t('applianceDetail.table.actuator')}</span>
                                <span>{t('applianceDetail.table.controller')}</span>
                                <span>{t('applianceDetail.table.type')}</span>
                                <span className={styles.ApplianceDetail__content__appliance__heading__status}>{t('applianceDetail.table.actuatorStatus')}</span>
                                <span className={styles.ApplianceDetail__content__appliance__heading__status}>{t('applianceDetail.table.controllerStatus')}</span>
                            </div>
                            <div className={styles.ApplianceDetail__content__appliance__row}>
                                <img src={getIconPath(selectedAppliance.icon, '.svg', 'controllers/black')} className={styles.ApplianceDetail__content__appliance__row__icon}/>
                                <span>{formatEntityId('appliance', selectedAppliance.id)}</span>
                                <span className={styles.ApplianceDetail__content__appliance__row__label}>
                                    {isEditingLabel ? (
                                        <input
                                            type="text"
                                            value={labelValue}
                                            autoFocus
                                            onChange={(event) => setLabelValue(event.target.value)}
                                            onKeyDown={handleLabelKeyDown}
                                            onBlur={saveLabel}
                                        />
                                    ) : (
                                        <span>{selectedAppliance.label}</span>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.ApplianceDetail__content__appliance__row__edit}
                                        aria-label="Edit appliance label"
                                        title="Edit appliance label"
                                        onClick={() => setIsEditingLabel(true)}
                                    >
                                        <img src={getIconPath('icoPen')} alt="" />
                                    </button>
                                </span>
                                <span>{getConnectedActuatorInformation(applianceActuator)}</span>
                                <span className={styles.ApplianceDetail__content__appliance__row__controllerInformation}>
                                    {getConnectedControllerInformation(connectedControllers)}
                                </span>
                                <span>{t(`applianceDetail.applianceUsages.${usage}`)}</span>
                                <img
                                    src={getIconPath(hasConnectedActuator ? 'ok' : 'error')}
                                    className={styles.ApplianceDetail__content__appliance__row__status}
                                    alt={t('applianceDetail.table.actuatorStatus')}
                                    title={t('applianceDetail.table.actuatorStatus')}
                                />
                                <img
                                    src={getIconPath(hasConnectedController ? 'ok' : 'error')}
                                    className={styles.ApplianceDetail__content__appliance__row__status}
                                    alt={t('applianceDetail.table.controllerStatus')}
                                    title={t('applianceDetail.table.controllerStatus')}
                                />
                            </div>
                        </div>
                        <div className={styles.ApplianceDetail__content__connections}>
                            <div className={styles.ApplianceDetail__content__connections__actuator}>
                                <h3>{t('applianceDetail.connected_actuator')}</h3>
                                <div 
                                    className={styles.ApplianceDetail__content__connections__actuator__item}
                                    onClick={() => applianceActuator && handleActuatorClick(applianceActuator)}    
                                >
                                    {applianceActuator === undefined ? (
                                        <div className={styles.ApplianceDetail__content__connections__actuator__fallback}>
                                            <p>{t('applianceDetail.noConnectedActuator')}</p>
                                            {actuators.length > 0 && 
                                                <>
                                                    <h4>{t('applianceDetail.actuators.heading')}</h4>
                                                    <div className={styles.ApplianceDetail__content__connections__available__list}>
                                                        {actuators.map((a) => (
                                                            <div
                                                                key={a.id}
                                                                className={styles.ApplianceDetail__content__connections__available__list__item}
                                                                onClick={() => handleActuatorClick(a)}
                                                            >
                                                                <img className={styles.ApplianceDetail__content__connections__available__list__item__actuator} src={addPublicURL(`/assets/actuators/${a.refImg}`)} alt={a.name} />
                                                                <p>{t('applianceDetail.table.id')}: {formatOptionalEntityId('actuator', a.id)}</p>
                                                                <p>{a.type} | {a.name}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            }
                                        </div>
                                       
                                    ) : (
                                        <>
                                            <img src={addPublicURL(`/assets/actuators/${applianceActuator.refImg}`)}/>
                                            <p>{applianceActuator.type} | {applianceActuator.name}</p>
                                            <p>ID: {formatOptionalEntityId('actuator', applianceActuator.id)}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className={styles.ApplianceDetail__content__connections__separator}><span></span></div>
                            <div className={styles.ApplianceDetail__content__connections__controllers}>
                                <h3>{t('applianceDetail.connected_controllers')}</h3>
                                {connectedControllers.length === 0 ? (
                                    <div className={styles.ApplianceDetail__content__connections__controllers__row__fallback}>
                                            <p>{t('applianceDetail.noConnectedControllers')}</p>
                                            {controllers.length > 0 && 
                                                <>
                                                    <h4>{t('applianceDetail.controllers.heading')}</h4>
                                                    <div className={styles.ApplianceDetail__content__connections__available__list}>
                                                        {controllers.map((c) => (
                                                            <div
                                                                key={c.id}
                                                                className={styles.ApplianceDetail__content__connections__available__list__item}
                                                                onClick={() => handleControllerClick(c.id, c.type)}
                                                            >
                                                                <img 
                                                                    src={c.configuredImageName ? `${process.env.REACT_APP_ICONS_URL}/source/userpanels/${c.configuredImageName}` : addPublicURL(`/assets/controllers/preview/${c.previewImage}-${c.color}.png`)}
                                                                    alt={c.name} 
                                                                />
                                                                <br />
                                                                <p>{t('applianceDetail.table.id')}: {formatEntityId('controller', c.id)}</p>
                                                                <p>{c.name + c.colorShortcut}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            }
                                    </div>
                                ) : (
                                    <div className={styles.ApplianceDetail__content__connections__controllers__row}>
                                            {connectedControllers.map((controller) => (
                                                <div 
                                                    key={controller.id} 
                                                    className={styles.ApplianceDetail__content__connections__controllers__row__item}
                                                    onClick={() => handleControllerClick(controller.id, controller.type)}
                                                >
                                                    <div className={styles.ApplianceDetail__content__connections__controllers__row__item__image}
                                                        
                                                    >
                                                        {controller.configuredImageName ? (
                                                            <img src={`${process.env.REACT_APP_ICONS_URL}/source/userpanels/${controller.configuredImageName}`} alt={controller.name + controller.colorShortcut} />
                                                        ) : (
                                                            // <>
                                                            //     <p>{t('applianceDetail.no_design')}</p>
                                                            //     <span>{t('applianceDetail.no_design_description')}</span>
                                                            // </>
                                                            <img src={addPublicURL(`/assets/controllers/preview/${controller.previewImage + '-' + controller.color}.png`)}/>
                                                        )}
                                                    </div>
                                                    <p>{controller.name + controller.colorShortcut}</p>
                                                    <p>ID: {formatEntityId('controller', controller.id)}</p>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.ApplianceDetail__wrapper__save}>
                        <Button onClick={() => dispatch(setShowApplianceDetail(false))} size="lg" variant="fill">{t('apply')}</Button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ApplianceDetail;